import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const languages = [
  { code: 'sr', name: 'Serbian', flag: 'https://flagcdn.com/w40/rs.png', alt: '🇷🇸' },
  { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/gb.png', alt: '🇬🇧' },
  { code: 'ru', name: 'Russian', flag: 'https://flagcdn.com/w40/ru.png', alt: '🇷🇺' }
];

export default function Header() {
  const { language, setLanguage, t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogoClick = (e) => {
    e.preventDefault();
    window.open('https://prirodanmed.com/', '_blank');
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-card/95 backdrop-blur-sm shadow-md border-b border-border">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://customer-assets.emergentagent.com/job_static-website-1/artifacts/v08vnpx2_Logo%20samo%20p%C4%8Delica.jpg" 
              alt="Markovic Farm Logo" 
              className="h-14 w-14 object-contain"
            />
            <span className="text-xl font-semibold text-foreground hidden sm:block">
              Prirodan Med
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('nav.home')}
            </Link>
            <Link 
              to="/about" 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('nav.about')}
            </Link>
            <Link 
              to="/products" 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('nav.products')}
            </Link>
            <Link 
              to="/contact" 
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('nav.contact')}
            </Link>
            {isAdmin && (
              <Link 
                to="/admin" 
                className="text-foreground hover:text-primary transition-colors font-medium"
              >
                {t('nav.admin')}
              </Link>
            )}
          </nav>

          {/* Language Switcher & Contact Button */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="hidden md:flex items-center gap-2 bg-secondary rounded-lg p-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-3 py-2 rounded-md transition-all ${
                    language === lang.code
                      ? 'bg-primary shadow-md scale-110'
                      : 'opacity-60 hover:opacity-100 hover:scale-105'
                  }`}
                  title={lang.name}
                  aria-label={`Switch to ${lang.name}`}
                >
                  <img 
                    src={lang.flag} 
                    alt={lang.alt}
                    className="w-8 h-6 object-cover rounded"
                  />
                </button>
              ))}
            </div>

            {/* Contact Button */}
            <Button 
              onClick={() => navigate('/contact')}
              className="hidden md:inline-flex btn-honey"
              size="sm"
            >
              {t('nav.contact')}
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-foreground p-2 hover:bg-secondary rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card animate-fade-in">
            <nav className="flex flex-col gap-2 p-4">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-secondary transition-all px-4 py-3 rounded-lg font-medium"
              >
                {t('nav.home')}
              </Link>
              <Link 
                to="/about" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-secondary transition-all px-4 py-3 rounded-lg font-medium"
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/products" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-secondary transition-all px-4 py-3 rounded-lg font-medium"
              >
                {t('nav.products')}
              </Link>
              <Link 
                to="/contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-secondary transition-all px-4 py-3 rounded-lg font-medium"
              >
                {t('nav.contact')}
              </Link>
              {isAdmin && (
                <Link 
                  to="/admin" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-foreground hover:text-primary hover:bg-secondary transition-all px-4 py-3 rounded-lg font-medium"
                >
                  {t('nav.admin')}
                </Link>
              )}
              
              {/* Mobile Language Switcher */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setMobileMenuOpen(false);
                    }}
                    className={`flex-1 px-3 py-3 rounded-md transition-all ${
                      language === lang.code
                        ? 'bg-primary shadow-md scale-105'
                        : 'bg-secondary opacity-60 hover:opacity-100'
                    }`}
                    title={lang.name}
                    aria-label={`Switch to ${lang.name}`}
                  >
                    <img 
                      src={lang.flag} 
                      alt={lang.alt}
                      className="w-10 h-7 object-cover rounded mx-auto"
                    />
                  </button>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}